/**
 * Превръщане на избрания период в границите, които API-то очаква.
 *
 * Компонентът за дати връща низ „ГГГГ-ММ-ДД“ без час и без зона. Разговорите
 * се показват по софийско време, затова „28 септември“ във филтъра трябва да
 * значи софийския ден, не денонощието по UTC — иначе разговор в 01:30 сутринта
 * попада в предишния ден.
 */

import {
  isTimeOfDayRangeOrdered,
  parseTimeOfDay,
  sofiaWallClockToUtc,
  type TimeOfDay,
} from '@vidanalyser/shared';

/** Границите на един ден: [00:00:00, 23:59:59.999] софийско време, в UTC. */
function dayBoundary(dateString: string, edge: 'start' | 'end'): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (!match) return null;

  const [, year, month, day] = match;
  const instant = sofiaWallClockToUtc({
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: edge === 'start' ? 0 : 23,
    minute: edge === 'start' ? 0 : 59,
    second: edge === 'start' ? 0 : 59,
  });

  if (!instant) return null;
  return edge === 'start' ? instant : new Date(instant.getTime() + 999);
}

export function periodStart(dateString: string | null): Date | null {
  return dateString ? dayBoundary(dateString, 'start') : null;
}

export function periodEnd(dateString: string | null): Date | null {
  return dateString ? dayBoundary(dateString, 'end') : null;
}

/** Добавя периода към параметрите на заявката, ако е избран. */
export function appendPeriod(
  params: URLSearchParams,
  period: readonly [string | null, string | null],
): URLSearchParams {
  const from = periodStart(period[0]);
  const to = periodEnd(period[1]);

  if (from) params.set('from', from.toISOString());
  if (to) params.set('to', to.toISOString());

  return params;
}

/** Часът от филтъра: „ЧЧ:ММ“ или празно, докато още не е избран. */
export type TimeRange = readonly [string | null, string | null];

/**
 * Непълната стойност значи „още се въвежда“, не „грешна“.
 *
 * Компонентът връща „12:“ между натискането на часа и на минутите; ако това се
 * смяташе за грешка, съобщението би мигало при всяко въвеждане.
 */
function complete(value: string | null): { value: string; time: TimeOfDay } | null {
  if (!value) return null;

  const time = parseTimeOfDay(value);
  return time ? { value, time } : null;
}

/**
 * Съобщението за невалиден диапазон на часа, или `null`.
 *
 * Обърнат диапазон (22:00–02:00) би минал през полунощ и би върнал разговори от
 * следващия ден, което противоречи на избраната дата.
 */
export function timeRangeError(range: TimeRange): string | null {
  const from = complete(range[0]);
  const to = complete(range[1]);
  if (!from || !to) return null;

  return isTimeOfDayRangeOrdered(from.time, to.time)
    ? null
    : 'Началният час трябва да е преди крайния';
}

/** Добавя диапазона на часа към параметрите на заявката, ако е избран. */
export function appendTimeRange(params: URLSearchParams, range: TimeRange): URLSearchParams {
  const from = complete(range[0]);
  const to = complete(range[1]);

  if (from) params.set('fromTime', from.value);
  if (to) params.set('toTime', to.value);

  return params;
}
